import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Button,
  Row,
  Col,
  message,
  Select,
} from "antd";
import axiosInstance from "../../services/axiosInstance";
import { catchError } from "../../utils/errorHandler";
import moment from "moment";
import usePermissions from "../../hooks/usePermissions";
import { useSelector } from "react-redux";
import hierarchyService from "../../services/hierarchyService";

const { Option } = Select;

const OperationdataInsights = ({ handleClose }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();

  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const canDownloadAnyOpsSheet = hasPermission("can_download_any_ops_sheet");
  const canDownloadOpsSheet = hasPermission("can_download_ops_sheet");
  const userEmail = useSelector((state) => state?.auth?.user?.user?.email);

  const downloadFile = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (canDownloadAnyOpsSheet) {
        setTeamLoading(true);
        try {
          const result = await hierarchyService.fetchOpsHierarchy();
          if (result.success && result.data) {
            setTeamMembers(result.data.data.hierarchy || []);
          }
        } catch (error) {
          console.error("Error fetching team members:", error);
        } finally {
          setTeamLoading(false);
        }
      }
    };
    fetchTeamMembers();
  }, [canDownloadAnyOpsSheet]);

  const handleFinish = async (values) => {
    setLoading(true);

    try {
      const from_date = values.dates[0].format("YYYY-MM-DD");
      const to_date = values.dates[1].format("YYYY-MM-DD");

      // Handle emails based on permission
      let emails = [];
      if (canDownloadAnyOpsSheet) {
        emails = values.emails || [];
      } else if (canDownloadOpsSheet) {
        emails = [values.email]; // Single email input
      }

      // ✅ BACKEND EXPECTS COMMA-SEPARATED STRING
      const params = {
        from_date,
        to_date,
        email: emails.join(","), // 👈 FIX
      };

      const response = await axiosInstance.get("/task/v2/ops-sheet/", {
        params,
        responseType: "blob",
      });

      const contentType = response.headers["content-type"] || "";

      // ❌ Backend JSON error
      if (contentType.includes("application/json")) {
        const text = await response.data.text();
        const err = JSON.parse(text);
        message.error(err.message || "Backend error");
        return;
      }

      // ✅ MULTIPLE → ZIP
      if (emails.length > 1) {
        downloadFile(
          new Blob([response.data], { type: "application/zip" }),
          `ops-sheet-${from_date}-${to_date}.zip`
        );
        message.success("ZIP downloaded successfully");
        handleClose();
        return;
      }

      // ✅ SINGLE → XLSX
      if (emails.length === 1) {
        downloadFile(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `${emails[0].split("@")[0]}-${from_date}-${to_date}.xlsx`
        );
        message.success("Report downloaded successfully");
        handleClose();
        return;
      }
    } catch (error) {
      catchError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <Row style={{ width: "100%" }}>
        {(hasPermission("can_download_ops_sheet") ||
          hasPermission("can_download_any_ops_sheet")) && (
          <Col span={24}>
            <Form.Item
              label="Select Date Range"
              name="dates"
              rules={[{ required: true, message: "Please select date range" }]}
            >
              <DatePicker.RangePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row style={{ width: "100%" }}>
        {canDownloadAnyOpsSheet ? (
          <Col span={24}>
            <Form.Item
              label="Select Team Members"
              name="emails"
              rules={[
                {
                  required: true,
                  message:
                    "Please select at least one team member or enter an email",
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Select team members or type email"
                loading={teamLoading}
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                tokenSeparators={[","]}
                style={{ width: "100%" }}
              >
                {teamMembers.map((member) => (
                  <Option key={member.email} value={member.email}>
                    {member.full_name || member.email}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        ) : canDownloadOpsSheet ? (
          <Col span={24}>
            <Form.Item
              label="Email"
              name="email"
              initialValue={userEmail}
              rules={[
                {
                  required: true,
                  message: "Please enter an email",
                },
                {
                  type: "email",
                  message: "Please enter a valid email",
                },
              ]}
            >
              <Input placeholder="Enter email" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        ) : null}
      </Row>

      <div
        style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
      >
        <Button type="primary" htmlType="submit" loading={loading}>
          Submit
        </Button>
      </div>
    </Form>
  );
};

export default OperationdataInsights;
